import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ClanNotifDTO, ClanNotifType } from '@/clan/clanApi';

interface LiveNotif {
  type: ClanNotifType;
  data: Record<string, unknown>;
  createdAt: number;
}

interface ClanNotifState {
  /** Most-recent-first inbox, capped so the persisted blob stays small. */
  items: ClanNotifDTO[];
  /** Unread count — drives the Home red dot. Persisted so it survives reload. */
  unread: number;
  /** Replace the inbox from the server (on app load / clan-screen open). */
  hydrate: (items: ClanNotifDTO[], unread: number) => void;
  /** A live socket notification arrived — prepend + bump unread. */
  pushLive: (n: LiveNotif) => void;
  /** Mark everything read locally (call alongside the server mark-read). */
  markAllRead: () => void;
}

const MAX_ITEMS = 80;

export const useClanNotifStore = create<ClanNotifState>()(
  persist(
    (set) => ({
      items: [],
      unread: 0,
      hydrate: (items, unread) => set({ items: items.slice(0, MAX_ITEMS), unread }),
      pushLive: (n) =>
        set((s) => ({
          items: [
            {
              id: `live-${n.createdAt}-${Math.random().toString(36).slice(2, 7)}`,
              type: n.type,
              data: n.data,
              readAt: null,
              createdAt: n.createdAt,
            },
            ...s.items,
          ].slice(0, MAX_ITEMS),
          unread: s.unread + 1,
        })),
      markAllRead: () =>
        set((s) => ({
          unread: 0,
          items: s.items.map((i) => (i.readAt ? i : { ...i, readAt: Date.now() })),
        })),
    }),
    { name: 'dm-clan-notif' },
  ),
);
